import { useContext } from "react";
import { animated, useSpring, config } from "react-spring";
import { PagesManagementContext, IPagesManagementContext } from "../../routes/builderContexts";
import { ActiveIcon, NotActiveIcon, unavailableIcon } from "./DrawerSVGs";
import WarningIcon from "../WarningIcon";

interface IStandardlisterProps {
  pagePullout: boolean;
  switchPage: (i: number, flag: string) => void;
  flagDirection: string;
  index: number;
  name: string;
  modes: number[];
  microLessonOrder?: number;
  selected?: boolean;
  displayedPageNumber?: number | null;
  reviewChanges?: boolean;
}
interface IStandardPanelProps {
  index: number;
  modes: number[];
  name: string;
  flagDirection: string;
  microLessonOrder?: number;
}

function isWarningOrScorePage(name: string) {
  if (name === "Warning Page" || name === "Score Page") {
    return true;
  }
}

const StandardPageListerPanel = (props: Readonly<IStandardPanelProps>) => {
  const pagesManagementContext: IPagesManagementContext = useContext(PagesManagementContext);
  const { lessonPages, lessonModes } = pagesManagementContext;

  const classOptions = ["I", "P", "T"];
  function renderIcon(id: number) {
    const index = props.index;
    const lessonModeIds = props.microLessonOrder
      ? lessonPages[props.microLessonOrder].pageManifest?.Pages[index].lessonModeIds
      : lessonPages[index].lessonModeIds;

    if (lessonModeIds?.includes(id)) {
      return <ActiveIcon />;
    }

    if (!isWarningOrScorePage(props.name)) {
      return NotActiveIcon;
    }

    return unavailableIcon("#ABB2BA");
  }

  return (
    <div className="lessonModes">
      {lessonModes.map((el, i) => {
        return (
          <div key={i} className={`option-${classOptions[i]}`}>
            {renderIcon(el.lessonModeId)}
          </div>
        );
      })}
    </div>
  );
};

const StandardPageLister = (props: IStandardlisterProps) => {
  const pagesManagementContext = useContext(PagesManagementContext);
  const numPages = pagesManagementContext.lessonPages.length;
  const microLesson = typeof props.microLessonOrder === "number";

  const getBorderStyle = () => {
    if (props.flagDirection === "start" && props.selected) {
      return "var(--highlighted-color) 3px solid";
    }

    return "none";
  };

  const sliderSpring = useSpring({
    config: config.default,
    width: props.pagePullout ? 450 : 200,
    opacity: props.pagePullout ? 1 : 0,
    x: props.pagePullout ? 262 : 0,
  });

  if (props.flagDirection === "title") {
    return (
      <div
        style={{
          height: "50px",
        }}
        className="pages-icons-container lesson-box"
      >
        <div className="lessonPages-TitleTab">
          <p>Pages</p>
          <p>{numPages}</p>
        </div>

        {props.pagePullout && (
          <animated.div
            style={{
              transform: sliderSpring.x.to((x) => `translate3d(${x}px,-100%, -1px)`),
              opacity: sliderSpring.opacity.to((opacity) => opacity),
            }}
            className="title-pop-out"
          >
            <div>
              <div style={{ fontSize: "0.85rem" }}>Pg No.</div>
              <p>Demo</p>
              <p>Practice</p>
              <p>Perform</p>
            </div>
          </animated.div>
        )}
      </div>
    );
  }

  const containerStyle = `${props.selected ? "standard-pages selected" : "standard-pages"} divider-line`;

  return (
    <div
      style={{
        height: "50px",
        position: "inherit",
      }}
      className="pages-icons-container lesson-box"
    >
      <div className={`pageBox-container`}>
        <div
          key={props.index}
          onClick={(e) => {
            e.stopPropagation();

            // don't switch if its on the same page
            if (!props.selected) {
              props.switchPage(props.index, props.flagDirection);
            }
          }}
          className={containerStyle}
          style={{
            borderRight: getBorderStyle(),
            paddingLeft: microLesson ? 30 : undefined,
          }}
          title={props.name}
        >
          {props.name}
        </div>
        <WarningIcon visible={props.reviewChanges ?? false} tooltipId="Page need review" />
      </div>
      {props.pagePullout && (
        <animated.div
          style={{
            transform: sliderSpring.x.to((x) => `translate3d(${x}px,-100%, -1px)`),
            opacity: sliderSpring.opacity.to((opacity) => opacity),
          }}
          onPointerUpCapture={() => {
            // don't switch if its on the same page
            if (!props.selected) {
              props.switchPage(props.index, props.flagDirection);
            }
          }}
          className={(props.selected ? "info-panel standard-selected" : "info-panel").concat(
            microLesson ? " microlesson" : "",
          )}
        >
          <div className="page-row">
            <div className="mode-page-numbers-col">{props.displayedPageNumber}</div>
            <StandardPageListerPanel
              index={props.index}
              modes={props.modes}
              name={props.name}
              flagDirection={props.flagDirection}
              microLessonOrder={props.microLessonOrder}
            />
          </div>
        </animated.div>
      )}
    </div>
  );
};

export default StandardPageLister;
